import { OrderApi } from '@/api';
import { ClientOrderDetails } from '@/components';
import { RoutePath } from '@/config';
import { useAuthContext, useLegalMutationsAllowed } from '@/hooks';
import type { OrderDetails } from '@/model';

import EmptyOrdersIcon from '../../assets/icons/empty-orders.svg?react';
import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import type { MouseEvent } from 'react';
import { Link, useLocation } from 'react-router-dom';

import { ClientCurrentOrderCard, CurrentOrdersSkeleton } from '../molecules';

const SORT_OPTIONS = [
  { label: 'Сначала новые', newestFirst: true },
  { label: 'Сначала старые', newestFirst: false },
];

export const ClientCurrentOrders = () => {
  const { isAuthorized } = useAuthContext();
  const { allowed: legalMutationsAllowed, requireConsent } =
    useLegalMutationsAllowed();
  // После оформления визард передаёт id созданного заказа — открываем его.
  const location = useLocation();
  const createdOrderId = (location.state as { orderId?: number } | null)
    ?.orderId;
  const [orderId, setOrderId] = useState<number | undefined>(createdOrderId);

  const handleCreateOrderClick = (event: MouseEvent<HTMLAnchorElement>) => {
    if (!legalMutationsAllowed) {
      event.preventDefault();
      requireConsent();
    }
  };
  const { data: activeOrders = [], isLoading: isActiveLoading } = useQuery({
    queryKey: [OrderApi.KEY, 'getActive'],
    queryFn: OrderApi.getActive,
    enabled: isAuthorized,
  });
  const { data: notPaidOrders = [], isLoading: isNotPaidLoading } = useQuery({
    queryKey: [OrderApi.KEY, 'getNotPaid'],
    queryFn: OrderApi.getNotPaid,
    enabled: isAuthorized,
  });

  // Сортировка объединённого списка по дате оформления заказа.
  const [isNewestFirst, setIsNewestFirst] = useState(true);

  const allCurrentOrders = useMemo(() => {
    const getTime = (order: OrderDetails) =>
      (order.create_date ?? order.status_date)?.getTime() ?? 0;

    return [...notPaidOrders, ...activeOrders].sort((a, b) =>
      isNewestFirst ? getTime(b) - getTime(a) : getTime(a) - getTime(b),
    );
  }, [notPaidOrders, activeOrders, isNewestFirst]);

  if (orderId) {
    return (
      <ClientOrderDetails
        orderId={orderId}
        onBack={() => setOrderId(undefined)}
      />
    );
  }

  // Пока идёт проверка авторизации или грузятся списки — заглушки вместо
  // пустого экрана и мигания «нет текущих заказов».
  if (!isAuthorized || isActiveLoading || isNotPaidLoading) {
    return <CurrentOrdersSkeleton />;
  }

  if (allCurrentOrders.length === 0) {
    return (
      <div className="flex size-full flex-col items-center justify-center gap-[32px] self-stretch rounded-[16px] bg-[#f1f7fa] p-[32px]">
        <div className="inline-flex items-center justify-start gap-[10px] rounded-[100px] bg-white p-[16px]">
          <EmptyOrdersIcon className="size-[48px] text-[#151515]" />
        </div>
        <p className="text-center font-['Ubuntu'] text-[20px] font-normal leading-[30px] text-[#151515] opacity-50">
          У вас пока нет текущих заказов, <br /> но вы можете оформить его
          сейчас по кнопке ниже
        </p>
        <Link
          to={RoutePath.ClientCreateOrder}
          onClick={handleCreateOrderClick}
          aria-disabled={!legalMutationsAllowed}
          className={`inline-flex h-[60px] items-center justify-start gap-[10px] rounded-[12px] px-[32px] py-[20px] transition-colors ${
            legalMutationsAllowed
              ? 'bg-[#0788f1] hover:bg-[#0670c4]'
              : 'cursor-not-allowed bg-[#0788f1] opacity-50'
          }`}
        >
          <span className="text-center font-['Ubuntu'] text-[16px] font-bold leading-[20px] text-white">
            Сделать заказ
          </span>
        </Link>
      </div>
    );
  }

  if (allCurrentOrders.length === 1) {
    return <ClientOrderDetails orderId={allCurrentOrders[0].id} />;
  }

  return (
    <div className="flex w-full flex-col items-start gap-[20px]">
      <div className="flex w-full flex-wrap items-center justify-end gap-[8px]">
        <span className="text-[14px] font-normal leading-5 text-[#171717] opacity-40">
          Сортировка по дате:
        </span>
        <div className="inline-flex items-center gap-[4px]">
          {SORT_OPTIONS.map((option) => (
            <button
              key={option.label}
              type="button"
              onClick={() => setIsNewestFirst(option.newestFirst)}
              className={`flex h-[28px] items-center justify-center rounded-[100px] px-[12px] py-[4px] text-[12px] font-medium leading-4 transition-colors ${
                isNewestFirst === option.newestFirst
                  ? 'bg-[#151515] text-white'
                  : 'bg-white text-[#171717] hover:bg-[#e4f0f7]'
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>
      {allCurrentOrders.map((order) => (
        <ClientCurrentOrderCard
          key={order.id}
          order={order as any}
          onClick={() => setOrderId(order.id)}
        />
      ))}
    </div>
  );
};
